import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import { AppDataState, AppData, ApiError } from "./types/appdata-types";

const initialState: AppDataState = {
  data: {}, // Original app data
  loading: false,
  error: null,
  success: false,
  isSearching: false,
  searchQuery: "",
  searchResults: {}, // Separate search results
  searchSuggestions: [], // Separate search suggestions
};

const API_URL = process.env.REACT_APP_API_URL;

// ✅ Thunk to fetch backend site-wide data
export const fetchAppData = createAsyncThunk<
  AppData,
  void,
  { rejectValue: ApiError; state: RootState }
>("appData/fetch", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;

  try {
    const headers: any = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await axios.get(`${API_URL}/app-data`, { headers });

    // The actual useful data is a JSON string → parse it
    const parsedData =
      typeof res.data.data === "string"
        ? JSON.parse(res.data.data)
        : res.data.data;

    return parsedData;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch app data" }
    );
  }
});

// New thunk for search suggestions only
export const searchAppData = createAsyncThunk<
  { query: string; results: any; suggestions: Array<any> },
  string,
  { rejectValue: ApiError; state: RootState }
>("appData/search", async (query: string, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;

  try {
    const headers: any = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.get(`${API_URL}/app-data/search`, {
      params: { query },
      headers,
    });

    // Parse the response data
    const parsedData =
      typeof response.data.data === "string"
        ? JSON.parse(response.data.data)
        : response.data.data;

    // Extract suggestions from the search results
    const suggestions = extractSearchSuggestions(parsedData, query);

    return {
      query,
      results: parsedData, // Full search results for search page
      suggestions: suggestions, // Only suggestions for dropdown
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || { message: "Search failed" }
    );
  }
});

// Helper function to extract search suggestions
const extractSearchSuggestions = (searchData: any, query: string): Array<any> => {
  const suggestions: Array<any> = [];
  
  if (!searchData) return suggestions;

  const categories = [
    { key: "most_popular", title: "Most Popular Today" },
    { key: "topup_plans", title: "Direct Game Topup" },
    { key: "game_cards", title: "Game Cards" },
    { key: "gift_cards", title: "Gift Cards" },
    { key: "data", title: "Data Services" },
    { key: "airtime", title: "Airtime Services" },
    { key: "cable", title: "Cable TV" },
    { key: "electric", title: "Electric Payment" },
  ];

  categories.forEach(({ key, title }) => {
    if (searchData[key]?.providers) {
      searchData[key].providers.forEach((provider: any) => {
        // Add provider match
        if (provider.name?.toLowerCase().includes(query.toLowerCase())) {
          suggestions.push({
            id: `provider-${provider.name}-${key}`,
            name: provider.name,
            category: title,
            categoryKey: key,
            image_url: provider.image_url,
            type: 'provider'
          });
        }
        
        // Add plan matches
        if (provider.plans) {
          provider.plans.forEach((plan: any) => {
            if (plan.name?.toLowerCase().includes(query.toLowerCase())) {
              suggestions.push({
                id: `plan-${provider.name}-${plan.name}-${key}`,
                name: `${provider.name} - ${plan.name}`,
                category: title,
                categoryKey: key,
                image_url: provider.image_url,
                type: 'plan'
              });
            }
          });
        }
      });
    }
  });

  // Remove duplicates and limit to 8 suggestions
  const uniqueSuggestions = suggestions.filter((suggestion, index, self) => 
    index === self.findIndex(s => s.id === suggestion.id)
  ).slice(0, 8);

  return uniqueSuggestions;
};

// ✅ Slice
const appDataSlice = createSlice({
  name: "appData",
  initialState,
  reducers: {
    resetAppData: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.data = {};
      state.isSearching = false;
      state.searchQuery = "";
      state.searchResults = {};
      state.searchSuggestions = [];
    },
    clearSearch: (state) => {
      state.isSearching = false;
      state.searchQuery = "";
      state.searchResults = {};
      state.searchSuggestions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchAppData.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
      })
      .addCase(fetchAppData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch app data";
      })
      .addCase(searchAppData.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(searchAppData.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchQuery = action.payload.query;
        state.searchResults = action.payload.results;
        state.searchSuggestions = action.payload.suggestions;
        state.success = true;
      })
      .addCase(searchAppData.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload?.message || "Search failed";
        state.searchSuggestions = [];
      });
  },
});

export const { resetAppData, clearSearch } = appDataSlice.actions;
export default appDataSlice.reducer;